import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Building2, Users, Target, TrendingUp, DollarSign, Clock,
  Search, Filter, Plus, Phone, Mail, Calendar,
  CheckCircle, AlertCircle, Star, Edit, Trash2,
  BarChart3, PieChart, Activity, FileDown, Upload, Loader2, LogOut, PhoneCall, PhoneOff, History
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { mockStates } from '../data/mock';
import { useAuth } from '../contexts/AuthContext';

// Mongo round-trips datetimes without a timezone marker (naive, but the
// value is always UTC since the backend writes datetime.now(timezone.utc)).
// new Date() treats a marker-less ISO string as local time, silently
// shifting every timestamp by the browser's UTC offset - append 'Z' when
// the string doesn't already carry one so it's parsed as UTC and converted
// to local time correctly.
const formatTimestamp = (ts) => {
  if (!ts) return '';
  const iso = /[Zz]|[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`;
  return new Date(iso).toLocaleString();
};

const FACILITY_TYPES = [
  { value: 'luxury_malibu', label: 'Luxury (Malibu)' },
  { value: 'luxury_resort', label: 'Luxury Resort' },
  { value: 'corporate_chain', label: 'Corporate Chain' },
  { value: 'regional_network', label: 'Regional Network' },
  { value: 'independent_private', label: 'Independent / Private' },
  { value: 'hospital_based', label: 'Hospital-Based' },
  { value: 'outpatient_only', label: 'Outpatient Only' },
];

const EMPTY_PROSPECT = {
  facility_name: '',
  state: '',
  facility_type: '',
  website: '',
  main_phone: '',
  city: '',
  priority: 'medium',
  status: 'new',
  notes: '',
};

const CRMDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const [stats, setStats] = useState({
    total_prospects: 0,
    new_prospects: 0,
    qualified_prospects: 0,
    active_opportunities: 0,
    closed_won_this_month: 0,
    total_pipeline_value: 0,
    conversion_rate: 0
  });

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [exportingAll, setExportingAll] = useState(false);

  const [showAddTask, setShowAddTask] = useState(false);
  const [globalNewTask, setGlobalNewTask] = useState({ prospect_id: '', title: '', due_date: '', priority: 'medium' });
  const [addingGlobalTask, setAddingGlobalTask] = useState(false);

  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  const [activityDateFrom, setActivityDateFrom] = useState('');
  const [activityDateTo, setActivityDateTo] = useState('');
  const [activitySearch, setActivitySearch] = useState('');

  const [tasks, setTasks] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  const [showAddProspect, setShowAddProspect] = useState(false);
  const [newProspect, setNewProspect] = useState(EMPTY_PROSPECT);
  const [addingProspect, setAddingProspect] = useState(false);
  const [addProspectError, setAddProspectError] = useState('');

  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  const [showReports, setShowReports] = useState(false);
  const [reports, setReports] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);

  const [showActivity, setShowActivity] = useState(false);
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editFields, setEditFields] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [newInteraction, setNewInteraction] = useState({ interaction_type: 'phone', subject: '', description: '', outcome: '' });
  const [loggingInteraction, setLoggingInteraction] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', due_date: '', priority: 'medium' });
  const [addingTask, setAddingTask] = useState(false);

  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const [showCallbackRequests, setShowCallbackRequests] = useState(false);
  const [callbackRequests, setCallbackRequests] = useState([]);
  const [loadingCallbackRequests, setLoadingCallbackRequests] = useState(false);
  const [callbackSourceFilter, setCallbackSourceFilter] = useState('all');
  const [callbackHideContacted, setCallbackHideContacted] = useState(false);
  const [markingContactedId, setMarkingContactedId] = useState(null);

  const [showFacilityVerifications, setShowFacilityVerifications] = useState(false);
  const [facilityVerifications, setFacilityVerifications] = useState([]);
  const [loadingFacilityVerifications, setLoadingFacilityVerifications] = useState(false);
  const [verificationHideReviewed, setVerificationHideReviewed] = useState(false);
  const [markingReviewedId, setMarkingReviewedId] = useState(null);

  const [showBuyerQualifications, setShowBuyerQualifications] = useState(false);
  const [buyerQualifications, setBuyerQualifications] = useState([]);
  const [loadingBuyerQualifications, setLoadingBuyerQualifications] = useState(false);
  const [qualificationFilter, setQualificationFilter] = useState('all');

  const [showDoNotCall, setShowDoNotCall] = useState(false);
  const [doNotCallList, setDoNotCallList] = useState([]);
  const [loadingDoNotCall, setLoadingDoNotCall] = useState(false);
  const [newDncEntry, setNewDncEntry] = useState({ facility_name: '', state: '', main_phone: '', reason: '' });
  const [addingDnc, setAddingDnc] = useState(false);
  const [dncCopied, setDncCopied] = useState(false);

  const [showCallLogs, setShowCallLogs] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [loadingCallLogs, setLoadingCallLogs] = useState(false);
  const [callLogAgentFilter, setCallLogAgentFilter] = useState('all');
  const [expandedCallId, setExpandedCallId] = useState(null);

  const [liveCalls, setLiveCalls] = useState([]);
  const [showCampaignQueue, setShowCampaignQueue] = useState(false);
  const [campaignQueue, setCampaignQueue] = useState(null);
  const [loadingCampaignQueue, setLoadingCampaignQueue] = useState(false);
  const [campaignActionLoading, setCampaignActionLoading] = useState(false);
  const [accountBalance, setAccountBalance] = useState(null);
  const [newBalanceInput, setNewBalanceInput] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  });

  useEffect(() => {
    loadDashboardData();
    loadTasks();
    apiCall('/api/help-requests', { headers: authHeaders() })
      .then((data) => setCallbackRequests(data || []))
      .catch((error) => console.error('Error loading callback requests:', error));
    apiCall('/api/voice-agent/facility-verifications', { headers: authHeaders() })
      .then((data) => setFacilityVerifications(data || []))
      .catch((error) => console.error('Error loading facility verifications:', error));
    apiCall('/api/voice-agent/buyer-qualifications', { headers: authHeaders() })
      .then((data) => setBuyerQualifications(data || []))
      .catch((error) => console.error('Error loading buyer qualifications:', error));
    apiCall('/api/voice-agent/do-not-call', { headers: authHeaders() })
      .then((data) => setDoNotCallList(data || []))
      .catch((error) => console.error('Error loading do-not-call list:', error));
    apiCall('/api/voice-agent/campaign-queue', { headers: authHeaders() })
      .then((data) => setCampaignQueue(data))
      .catch((error) => console.error('Error loading campaign queue:', error));
  }, []);

  useEffect(() => {
    const pollLiveCalls = () => {
      apiCall('/api/voice-agent/live-calls', { headers: authHeaders() })
        .then((data) => setLiveCalls(data || []))
        .catch((error) => console.error('Error loading live calls:', error));
    };
    pollLiveCalls();
    const interval = setInterval(pollLiveCalls, 8000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadProspects();
  }, [searchQuery, statusFilter, stateFilter, showArchived, page]);

  useEffect(() => {
    if (showActivity) loadActivity();
  }, [activityTypeFilter, activityDateFrom, activityDateTo]);

  const loadDashboardData = async () => {
    try {
      const data = await apiCall('/api/crm/dashboard/stats', { headers: authHeaders() });
      setStats(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const buildProspectParams = () => {
    const params = new URLSearchParams({ page: String(page), limit: '50' });
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter !== 'all') params.set('status_filter', statusFilter);
    if (stateFilter !== 'all') params.set('state', stateFilter);
    if (showArchived) params.set('archived', 'true');
    return params;
  };

  const loadProspects = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/crm/prospects?${buildProspectParams().toString()}`, { headers: authHeaders() });
      setProspects(data.prospects || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error loading prospects:', error);
      setProspects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => { setSearchQuery(value); setPage(1); };
  const handleStatusFilterChange = (value) => { setStatusFilter(value); setPage(1); };
  const handleStateFilterChange = (value) => { setStateFilter(value); setPage(1); };
  const handleToggleArchived = () => { setShowArchived((v) => !v); setPage(1); };

  const loadTasks = async () => {
    try {
      const data = await apiCall('/api/crm/tasks/my', { headers: authHeaders() });
      setTasks(data.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-gray-100 text-gray-800', text: 'New' },
      contacted: { color: 'bg-blue-100 text-blue-800', text: 'Contacted' },
      interested: { color: 'bg-yellow-100 text-yellow-800', text: 'Interested' },
      qualified: { color: 'bg-green-100 text-green-800', text: 'Qualified' },
      proposal_sent: { color: 'bg-purple-100 text-purple-800', text: 'Proposal Sent' },
      negotiating: { color: 'bg-orange-100 text-orange-800', text: 'Negotiating' },
      closed_won: { color: 'bg-green-100 text-green-800', text: 'Closed Won' },
      closed_lost: { color: 'bg-red-100 text-red-800', text: 'Closed Lost' },
      on_hold: { color: 'bg-gray-100 text-gray-800', text: 'On Hold' }
    };
    
    const config = statusConfig[status] || statusConfig.new;
    
    return (
      <Badge className={`${config.color} border-0`}>
        {config.text}
      </Badge>
    );
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'high':
        return <Star className="h-4 w-4 text-orange-500" />;
      case 'medium':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <Clock className="h-4 w-4 text-gray-400" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleSelectProspect = (prospectId) => {
    setSelectedProspects(prev => 
      prev.includes(prospectId)
        ? prev.filter(id => id !== prospectId)
        : [...prev, prospectId]
    );
  };

  const handleBulkAction = (action) => {
    setBulkAction(action);
    setBulkValue('');
    setShowBulkDialog(true);
  };

  const handleBulkConfirm = async () => {
    if (!bulkValue.trim()) return;
    setBulkSubmitting(true);
    try {
      const value = bulkAction === 'add_tags'
        ? bulkValue.split(',').map((t) => t.trim()).filter(Boolean)
        : bulkValue;

      await apiCall('/api/crm/prospects/bulk-update', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          prospect_ids: selectedProspects,
          action: bulkAction,
          value,
        }),
      });
      setShowBulkDialog(false);
      setSelectedProspects([]);
      loadProspects();
      loadDashboardData();
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleAddProspect = async (e) => {
    e.preventDefault();
    setAddProspectError('');

    if (!newProspect.facility_name.trim() || !newProspect.state || !newProspect.facility_type) {
      setAddProspectError('Facility name, state, and facility type are required.');
      return;
    }

    setAddingProspect(true);
    try {
      await apiCall('/api/crm/prospects', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...newProspect,
          website: newProspect.website || null,
          main_phone: newProspect.main_phone || null,
          city: newProspect.city || null,
          notes: newProspect.notes || null,
        }),
      });
      setShowAddProspect(false);
      setNewProspect(EMPTY_PROSPECT);
      loadProspects();
      loadDashboardData();
    } catch (error) {
      console.error('Error adding prospect:', error);
      setAddProspectError('Failed to add prospect. Please try again.');
    } finally {
      setAddingProspect(false);
    }
  };

  const handleArchiveProspect = async (prospectId) => {
    try {
      await apiCall(`/api/crm/prospects/${prospectId}/archive`, { method: 'PUT', headers: authHeaders() });
      loadProspects();
      loadDashboardData();
    } catch (error) {
      console.error('Error archiving prospect:', error);
    }
  };

  const handleRestoreProspect = async (prospectId) => {
    try {
      await apiCall(`/api/crm/prospects/${prospectId}/restore`, { method: 'PUT', headers: authHeaders() });
      loadProspects();
      loadDashboardData();
    } catch (error) {
      console.error('Error restoring prospect:', error);
    }
  };

  const handleDeleteProspect = async (prospectId, facilityName) => {
    if (!window.confirm(`Permanently delete "${facilityName}"? This cannot be undone.`)) return;
    try {
      await apiCall(`/api/crm/prospects/${prospectId}`, { method: 'DELETE', headers: authHeaders() });
      loadProspects();
      loadDashboardData();
    } catch (error) {
      console.error('Error deleting prospect:', error);
    }
  };

  const csvEscape = (value) => {
    const str = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const EXPORT_COLUMNS = ['facility_name', 'state', 'facility_type', 'status', 'priority', 'main_phone', 'website', 'city', 'estimated_annual_revenue', 'notes'];

  const exportRows = (rows, filenameSuffix) => {
    const csvRows = [EXPORT_COLUMNS.join(',')];
    rows.forEach((p) => {
      csvRows.push(EXPORT_COLUMNS.map((c) => csvEscape(p[c])).join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prospects-${filenameSuffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const DNC_EXPORT_COLUMNS = ['facility_name', 'state', 'main_phone', 'reason', 'source', 'created_at'];

  const handleExportDnc = () => {
    const csvRows = [DNC_EXPORT_COLUMNS.join(',')];
    doNotCallList.forEach((d) => {
      csvRows.push(DNC_EXPORT_COLUMNS.map((c) => csvEscape(d[c])).join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `do-not-call-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyDnc = async () => {
    // Tab-separated so a paste into a spreadsheet lands in separate columns.
    const text = doNotCallList
      .map((d) => [d.facility_name || '', d.state || '', d.main_phone || '', d.reason || ''].join('\t'))
      .join('\n');
    await navigator.clipboard.writeText(text);
    setDncCopied(true);
    setTimeout(() => setDncCopied(false), 2000);
  };

  const handleExportPage = () => exportRows(prospects, 'page');

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      const base = buildProspectParams();
      base.set('page', '1');
      base.set('limit', '100');
      const firstPage = await apiCall(`/api/crm/prospects?${base.toString()}`, { headers: authHeaders() });
      let all = firstPage.prospects || [];
      const pages = firstPage.total_pages || 1;
      for (let p = 2; p <= pages; p++) {
        base.set('page', String(p));
        const data = await apiCall(`/api/crm/prospects?${base.toString()}`, { headers: authHeaders() });
        all = all.concat(data.prospects || []);
      }
      exportRows(all, 'all');
    } catch (error) {
      console.error('Error exporting all prospects:', error);
    } finally {
      setExportingAll(false);
    }
  };

  const openReports = async () => {
    setShowReports(true);
    setLoadingReports(true);
    try {
      const data = await apiCall('/api/crm/dashboard/reports', { headers: authHeaders() });
      setReports(data);
    } catch (error) {
      console.error('Error loading reports:', error);
      setReports(null);
    } finally {
      setLoadingReports(false);
    }
  };

  const openActivity = async () => {
    setShowActivity(true);
    await loadActivity();
  };

  const loadActivity = async () => {
    setLoadingActivity(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (activityTypeFilter !== 'all') params.set('interaction_type', activityTypeFilter);
      if (activityDateFrom) params.set('date_from', new Date(activityDateFrom).toISOString());
      if (activityDateTo) params.set('date_to', new Date(`${activityDateTo}T23:59:59`).toISOString());
      const data = await apiCall(`/api/crm/activity?${params.toString()}`, { headers: authHeaders() });
      setActivity(data.activity || []);
    } catch (error) {
      console.error('Error loading activity:', error);
      setActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const filteredActivity = activity.filter((a) =>
    !activitySearch || (a.facility_name || '').toLowerCase().includes(activitySearch.toLowerCase())
  );

  const openCallbackRequests = async () => {
    setShowCallbackRequests(true);
    setLoadingCallbackRequests(true);
    try {
      const data = await apiCall('/api/help-requests', { headers: authHeaders() });
      setCallbackRequests(data || []);
    } catch (error) {
      console.error('Error loading callback requests:', error);
      setCallbackRequests([]);
    } finally {
      setLoadingCallbackRequests(false);
    }
  };

  const handleMarkContacted = async (id) => {
    setMarkingContactedId(id);
    try {
      await apiCall(`/api/help-requests/${id}/contacted`, { method: 'PUT', headers: authHeaders() });
      setCallbackRequests((prev) => prev.map((r) => (r.id === id ? { ...r, contacted: true } : r)));
    } catch (error) {
      console.error('Error marking contacted:', error);
    } finally {
      setMarkingContactedId(null);
    }
  };

  const filteredCallbackRequests = callbackRequests.filter((r) => {
    if (callbackSourceFilter !== 'all' && r.source !== callbackSourceFilter) return false;
    if (callbackHideContacted && r.contacted) return false;
    return true;
  });

  const pendingCallbackCount = callbackRequests.filter((r) => !r.contacted).length;

  const openFacilityVerifications = async () => {
    setShowFacilityVerifications(true);
    setLoadingFacilityVerifications(true);
    try {
      const data = await apiCall('/api/voice-agent/facility-verifications', { headers: authHeaders() });
      setFacilityVerifications(data || []);
    } catch (error) {
      console.error('Error loading facility verifications:', error);
      setFacilityVerifications([]);
    } finally {
      setLoadingFacilityVerifications(false);
    }
  };

  const handleMarkVerificationReviewed = async (id) => {
    setMarkingReviewedId(id);
    try {
      await apiCall(`/api/voice-agent/facility-verifications/${id}/reviewed`, { method: 'PUT', headers: authHeaders() });
      setFacilityVerifications((prev) => prev.map((v) => (v.id === id ? { ...v, reviewed: true } : v)));
    } catch (error) {
      console.error('Error marking verification reviewed:', error);
    } finally {
      setMarkingReviewedId(null);
    }
  };

  const filteredFacilityVerifications = facilityVerifications.filter((v) =>
    !verificationHideReviewed || !v.reviewed
  );

  const pendingVerificationCount = facilityVerifications.filter((v) => !v.reviewed).length;

  const openBuyerQualifications = async () => {
    setShowBuyerQualifications(true);
    setLoadingBuyerQualifications(true);
    try {
      const data = await apiCall('/api/voice-agent/buyer-qualifications', { headers: authHeaders() });
      setBuyerQualifications(data || []);
    } catch (error) {
      console.error('Error loading buyer qualifications:', error);
      setBuyerQualifications([]);
    } finally {
      setLoadingBuyerQualifications(false);
    }
  };

  const filteredBuyerQualifications = buyerQualifications.filter((q) => {
    if (qualificationFilter === 'qualified') return q.qualified;
    if (qualificationFilter === 'decision_maker_unreachable') return !q.decision_maker_confirmed;
    if (qualificationFilter === 'not_interested') return q.decision_maker_confirmed && q.interested === false;
    return true;
  });

  const qualifiedBuyerCount = buyerQualifications.filter((q) => q.qualified).length;

  const AGENT_NAMES = {
    agent_b464c9c116429482cd1526707d: 'Manu',
    agent_4c1fa450c2a29088ba5bde405d: 'Mornock',
    agent_21fb8c47a48ec7d41b0144bafd: 'Mary',
  };

  const openCallLogs = async () => {
    setShowCallLogs(true);
    setLoadingCallLogs(true);
    try {
      const data = await apiCall('/api/voice-agent/call-logs', { headers: authHeaders() });
      setCallLogs(data || []);
    } catch (error) {
      console.error('Error loading call logs:', error);
      setCallLogs([]);
    } finally {
      setLoadingCallLogs(false);
    }
  };

  const filteredCallLogs = callLogs.filter((c) =>
    callLogAgentFilter === 'all' || c.agent_id === callLogAgentFilter
  );

  const openDoNotCall = async () => {
    setShowDoNotCall(true);
    setLoadingDoNotCall(true);
    try {
      const data = await apiCall('/api/voice-agent/do-not-call', { headers: authHeaders() });
      setDoNotCallList(data || []);
    } catch (error) {
      console.error('Error loading do-not-call list:', error);
      setDoNotCallList([]);
    } finally {
      setLoadingDoNotCall(false);
    }
  };

  const loadAccountBalance = async () => {
    try {
      const data = await apiCall('/api/voice-agent/account-balance', { headers: authHeaders() });
      setAccountBalance(data);
    } catch (error) {
      console.error('Error loading account balance:', error);
      setAccountBalance(null);
    }
  };

  const openCampaignQueue = async () => {
    setShowCampaignQueue(true);
    setLoadingCampaignQueue(true);
    try {
      const data = await apiCall('/api/voice-agent/campaign-queue', { headers: authHeaders() });
      setCampaignQueue(data);
    } catch (error) {
      console.error('Error loading campaign queue:', error);
      setCampaignQueue(null);
    } finally {
      setLoadingCampaignQueue(false);
    }
    loadAccountBalance();
  };

  const handleSaveBalance = async () => {
    const value = parseFloat(newBalanceInput);
    if (isNaN(value) || value < 0) return;
    setSavingBalance(true);
    try {
      await apiCall('/api/voice-agent/account-balance', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ balance_usd: value }),
      });
      setNewBalanceInput('');
      await loadAccountBalance();
    } catch (error) {
      console.error('Error saving account balance:', error);
    } finally {
      setSavingBalance(false);
    }
  };

  const handlePauseCampaign = async () => {
    setCampaignActionLoading(true);
    try {
      await apiCall('/api/voice-agent/campaign/pause', { method: 'POST', headers: authHeaders() });
      await openCampaignQueue();
    } catch (error) {
      console.error('Error pausing campaign:', error);
    } finally {
      setCampaignActionLoading(false);
    }
  };

  const handleResumeCampaign = async () => {
    setCampaignActionLoading(true);
    try {
      await apiCall('/api/voice-agent/campaign/resume', { method: 'POST', headers: authHeaders() });
      await openCampaignQueue();
    } catch (error) {
      console.error('Error resuming campaign:', error);
    } finally {
      setCampaignActionLoading(false);
    }
  };

  const handleRunCampaignNow = async () => {
    if (!window.confirm('Start today\'s campaign run right now? This places real outbound calls.')) return;
    setCampaignActionLoading(true);
    try {
      await apiCall('/api/voice-agent/campaign/run-now', { method: 'POST', headers: authHeaders() });
      await openCampaignQueue();
    } catch (error) {
      console.error('Error starting campaign run:', error);
      alert(error?.message || 'Failed to start campaign run');
    } finally {
      setCampaignActionLoading(false);
    }
  };

  const handleAddDnc = async () => {
    if (!newDncEntry.facility_name.trim()) return;
    setAddingDnc(true);
    try {
      await apiCall('/api/voice-agent/do-not-call/manual', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          facility_name: newDncEntry.facility_name,
          state: newDncEntry.state || null,
          main_phone: newDncEntry.main_phone || null,
          reason: newDncEntry.reason || null,
        }),
      });
      setNewDncEntry({ facility_name: '', state: '', main_phone: '', reason: '' });
      openDoNotCall();
    } catch (error) {
      console.error('Error adding to do-not-call list:', error);
    } finally {
      setAddingDnc(false);
    }
  };

  const openDetail = async (prospectId) => {
    setShowDetail(true);
    setLoadingDetail(true);
    setDetailData(null);
    try {
      const data = await apiCall(`/api/crm/prospects/${prospectId}`, { headers: authHeaders() });
      setDetailData(data);
      setEditFields({
        facility_name: data.prospect.facility_name || '',
        website: data.prospect.website || '',
        main_phone: data.prospect.main_phone || '',
        city: data.prospect.city || '',
        state: data.prospect.state || '',
        facility_type: data.prospect.facility_type || '',
        status: data.prospect.status || 'new',
        priority: data.prospect.priority || 'medium',
        notes: data.prospect.notes || '',
      });
      setNewInteraction({ interaction_type: 'phone', subject: '', description: '', outcome: '' });
      setNewTask({ title: '', due_date: '', priority: 'medium' });
    } catch (error) {
      console.error('Error loading prospect detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!detailData) return;
    setSavingEdit(true);
    try {
      await apiCall(`/api/crm/prospects/${detailData.prospect.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(editFields),
      });
      await openDetail(detailData.prospect.id);
      loadProspects();
    } catch (error) {
      console.error('Error saving prospect:', error);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleLogInteraction = async () => {
    if (!detailData || !newInteraction.subject.trim()) return;
    setLoggingInteraction(true);
    try {
      await apiCall('/api/crm/interactions', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          prospect_id: detailData.prospect.id,
          interaction_type: newInteraction.interaction_type,
          subject: newInteraction.subject,
          description: newInteraction.description || newInteraction.subject,
          outcome: newInteraction.outcome || null,
        }),
      });
      await openDetail(detailData.prospect.id);
      loadProspects();
    } catch (error) {
      console.error('Error logging interaction:', error);
    } finally {
      setLoggingInteraction(false);
    }
  };

  const handleAddTaskForProspect = async () => {
    if (!detailData || !newTask.title.trim() || !newTask.due_date) return;
    setAddingTask(true);
    try {
      await apiCall('/api/crm/tasks', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          prospect_id: detailData.prospect.id,
          title: newTask.title,
          due_date: new Date(newTask.due_date).toISOString(),
          priority: newTask.priority,
        }),
      });
      await openDetail(detailData.prospect.id);
      loadTasks();
    } catch (error) {
      console.error('Error adding task:', error);
    } finally {
      setAddingTask(false);
    }
  };

  const openAddTask = () => {
    setGlobalNewTask({ prospect_id: '', title: '', due_date: '', priority: 'medium' });
    setShowAddTask(true);
  };

  const handleAddGlobalTask = async () => {
    if (!globalNewTask.prospect_id || !globalNewTask.title.trim() || !globalNewTask.due_date) return;
    setAddingGlobalTask(true);
    try {
      await apiCall('/api/crm/tasks', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          prospect_id: globalNewTask.prospect_id,
          title: globalNewTask.title,
          due_date: new Date(globalNewTask.due_date).toISOString(),
          priority: globalNewTask.priority,
        }),
      });
      setShowAddTask(false);
      loadTasks();
    } catch (error) {
      console.error('Error adding task:', error);
    } finally {
      setAddingGlobalTask(false);
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => setImportText(String(evt.target.result || ''));
    reader.readAsText(file);
    e.target.value = '';
  };

  const parseImportCsv = (text) => {
    const lines = text.trim().split('\n').filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const values = line.split(',').map((v) => v.trim());
      const row = {};
      headers.forEach((h, i) => {
        if (values[i]) row[h] = values[i];
      });
      return row;
    }).filter((row) => row.facility_name && row.state);
  };

  const handleImportSubmit = async () => {
    setImportError('');
    const rows = parseImportCsv(importText);
    if (rows.length === 0) {
      setImportError('No valid rows found. Each row needs at least facility_name and state columns.');
      return;
    }
    setImporting(true);
    try {
      const result = await apiCall('/api/crm/prospects/import', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(rows),
      });
      setImportResult(result);
      loadProspects();
      loadDashboardData();
    } catch (error) {
      console.error('Error importing prospects:', error);
      setImportError('Import failed. Please check the CSV format and try again.');
    } finally {
      setImporting(false);
    }
  };

  // Filtering (search/status/state) now happens server-side in loadProspects,
  // so prospects already reflects the current filters.
  const filteredProspects = prospects;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">CRM Dashboard</h1>
              <p className="text-gray-600">Manage high-value treatment center prospects</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button variant="outline" onClick={handleExportPage}>
                <FileDown className="h-4 w-4 mr-2" />
                Export Page
              </Button>
              <Button variant="outline" onClick={handleExportAll} disabled={exportingAll}>
                {exportingAll ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Exporting...</>
                ) : (
                  <><FileDown className="h-4 w-4 mr-2" />Export All</>
                )}
              </Button>
              <Button onClick={() => setShowAddProspect(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Prospect
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Stats Cards */}
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Prospects</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_prospects}</p>
                    <p className="text-sm text-green-600">+{stats.new_prospects} this month</p>
                  </div>
                  <Building2 className="h-10 w-10 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Qualified Prospects</p>
                    <p className="text-3xl font-bold text-green-600">{stats.qualified_prospects}</p>
                    <p className="text-sm text-gray-600">{stats.active_opportunities} opportunities</p>
                  </div>
                  <Target className="h-10 w-10 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pipeline Value</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {formatCurrency(stats.total_pipeline_value)}
                    </p>
                    <p className="text-sm text-gray-600">{stats.closed_won_this_month} closed this month</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                    <p className="text-3xl font-bold text-orange-600">{stats.conversion_rate}%</p>
                    <p className="text-sm text-gray-600">New prospects to closed-won this month</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content - Prospects */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>High-Value Prospects</CardTitle>
                    <div className="flex items-center space-x-2">
                      {selectedProspects.length > 0 && (
                        <Select onValueChange={handleBulkAction}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Bulk Actions" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="update_status">Update Status</SelectItem>
                            <SelectItem value="update_priority">Update Priority</SelectItem>
                            <SelectItem value="assign_to">Assign To</SelectItem>
                            <SelectItem value="add_tags">Add Tags</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  
                  {/* Filters */}
                  <div className="flex flex-col md:flex-row gap-4 mt-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Search facilities..."
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="interested">Interested</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                        <SelectItem value="negotiating">Negotiating</SelectItem>
                        <SelectItem value="closed_won">Closed Won</SelectItem>
                        <SelectItem value="closed_lost">Closed Lost</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={stateFilter} onValueChange={handleStateFilterChange}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="all">All States</SelectItem>
                        {mockStates.map((s) => (
                          <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap px-1">
                      <input type="checkbox" checked={showArchived} onChange={handleToggleArchived} />
                      Show archived
                    </label>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-4">
                    {loading ? (
                      <div className="text-center py-8">Loading prospects...</div>
                    ) : filteredProspects.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No prospects found matching your criteria.
                      </div>
                    ) : (
                      filteredProspects.map((prospect) => (
                        <div 
                          key={prospect.id}
                          className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <input
                                type="checkbox"
                                checked={selectedProspects.includes(prospect.id)}
                                onChange={() => handleSelectProspect(prospect.id)}
                                className="mt-1"
                              />
                              
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h4 className="font-semibold text-gray-900">
                                    {prospect.facility_name}
                                  </h4>
                                  {getStatusBadge(prospect.status)}
                                  {getPriorityIcon(prospect.priority)}
                                </div>
                                
                                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                                  <div>State: {prospect.state}</div>
                                  <div>Type: {prospect.facility_type.replace('_', ' ')}</div>
                                  <div>Contacts: {prospect.contact_count}</div>
                                  <div>
                                    Est. Revenue: {prospect.estimated_annual_revenue
                                      ? formatCurrency(prospect.estimated_annual_revenue)
                                      : 'Not set'}
                                  </div>
                                </div>
                                
                                {prospect.tags && prospect.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {prospect.tags.map((tag, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                
                                <div className="flex items-center justify-between mt-3">
                                  <div className="text-xs text-gray-500">
                                    {prospect.last_contact_date 
                                      ? `Last contact: ${new Date(prospect.last_contact_date).toLocaleDateString()}`
                                      : 'No contact yet'
                                    }
                                  </div>
                                  
                                  <div className="flex items-center space-x-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={!prospect.main_phone}
                                      asChild={!!prospect.main_phone}
                                    >
                                      {prospect.main_phone ? (
                                        <a href={`tel:${prospect.main_phone}`}>
                                          <Phone className="h-3 w-3 mr-1" />
                                          Call
                                        </a>
                                      ) : (
                                        <>
                                          <Phone className="h-3 w-3 mr-1" />
                                          Call
                                        </>
                                      )}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => openDetail(prospect.id)}>
                                      <Edit className="h-3 w-3 mr-1" />
                                      View / Edit
                                    </Button>
                                    {!prospect.is_archived ? (
                                      <Button size="sm" variant="outline" onClick={() => handleArchiveProspect(prospect.id)}>
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Archive
                                      </Button>
                                    ) : (
                                      <>
                                        <Button size="sm" variant="outline" onClick={() => handleRestoreProspect(prospect.id)}>
                                          Restore
                                        </Button>
                                        <Button size="sm" variant="destructive" onClick={() => handleDeleteProspect(prospect.id, prospect.facility_name)}>
                                          Delete
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {!loading && filteredProspects.length > 0 && totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 mt-4 border-t">
                      <p className="text-sm text-gray-500">
                        Page {page} of {totalPages} ({totalCount} total)
                      </p>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                          Previous
                        </Button>
                        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* My Tasks */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    My Tasks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tasks.length === 0 ? (
                      <p className="text-gray-500 text-sm">No pending tasks</p>
                    ) : (
                      tasks.map((task) => (
                        <div key={task.id} className="p-3 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{task.title}</p>
                              <p className="text-xs text-gray-600">{task.prospect_name}</p>
                              <div className="flex items-center space-x-2 mt-2">
                                <Calendar className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-500">
                                  {new Date(task.due_date).toLocaleDateString()}
                                </span>
                                {getPriorityIcon(task.priority)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <Button className="w-full mt-4" variant="outline" size="sm" onClick={openAddTask}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {liveCalls.length > 0 && (
                    <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-green-800 mb-1">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        Live now ({liveCalls.length})
                      </div>
                      <div className="space-y-0.5">
                        {liveCalls.map((c) => (
                          <p key={c.call_id} className="text-xs text-green-900">
                            {AGENT_NAMES[c.agent_id] || 'Unknown agent'}{' '}
                            {c.direction === 'outbound' ? '→' : '←'}{' '}
                            {c.facility_name || (c.direction === 'outbound' ? c.to_number : c.from_number)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button className="w-full" variant="outline" onClick={() => setShowAddProspect(true)}>
                    <Building2 className="h-4 w-4 mr-2" />
                    Add New Prospect
                  </Button>
                  <Button className="w-full" variant="outline" onClick={() => setShowImport(true)}>
                    <Users className="h-4 w-4 mr-2" />
                    Import Contacts
                  </Button>
                  <Button className="w-full" variant="outline" onClick={openReports}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    View Reports
                  </Button>
                  <Button className="w-full" variant="outline" onClick={openActivity}>
                    <Activity className="h-4 w-4 mr-2" />
                    Activity Log
                  </Button>
                  <Button className="w-full justify-between" variant="outline" onClick={openCallbackRequests}>
                    <span className="flex items-center">
                      <PhoneCall className="h-4 w-4 mr-2" />
                      Callback Requests
                    </span>
                    {pendingCallbackCount > 0 && (
                      <Badge className="bg-red-100 text-red-800 border-0">{pendingCallbackCount}</Badge>
                    )}
                  </Button>
                  <Button className="w-full justify-between" variant="outline" onClick={openFacilityVerifications}>
                    <span className="flex items-center">
                      <Building2 className="h-4 w-4 mr-2" />
                      Facility Verifications
                    </span>
                    {pendingVerificationCount > 0 && (
                      <Badge className="bg-red-100 text-red-800 border-0">{pendingVerificationCount}</Badge>
                    )}
                  </Button>
                  <Button className="w-full justify-between" variant="outline" onClick={openBuyerQualifications}>
                    <span className="flex items-center">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Buyer Qualifications
                    </span>
                    {qualifiedBuyerCount > 0 && (
                      <Badge className="bg-green-100 text-green-800 border-0">{qualifiedBuyerCount}</Badge>
                    )}
                  </Button>
                  <Button className="w-full justify-between" variant="outline" onClick={openDoNotCall}>
                    <span className="flex items-center">
                      <PhoneOff className="h-4 w-4 mr-2" />
                      Do Not Call List
                    </span>
                    {doNotCallList.length > 0 && (
                      <Badge className="bg-gray-100 text-gray-800 border-0">{doNotCallList.length}</Badge>
                    )}
                  </Button>
                  <Button className="w-full justify-between" variant="outline" onClick={openCallLogs}>
                    <span className="flex items-center">
                      <History className="h-4 w-4 mr-2" />
                      Call Logs
                    </span>
                  </Button>
                  <Button className="w-full justify-between" variant="outline" onClick={openCampaignQueue}>
                    <span className="flex items-center">
                      <PhoneCall className="h-4 w-4 mr-2" />
                      Campaign Queue
                    </span>
                    {campaignQueue && (
                      <span className="flex items-center gap-1">
                        {campaignQueue.paused && <Badge className="bg-amber-100 text-amber-800 border-0">Paused</Badge>}
                        <Badge className="bg-blue-100 text-blue-800 border-0">
                          {campaignQueue.placed_count}/{campaignQueue.daily_limit}
                        </Badge>
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </div>

      <Dialog open={showAddProspect} onOpenChange={setShowAddProspect}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Prospect</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddProspect} className="space-y-4">
            {addProspectError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-700">{addProspectError}</AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="facility_name">Facility Name *</Label>
              <Input
                id="facility_name"
                value={newProspect.facility_name}
                onChange={(e) => setNewProspect((p) => ({ ...p, facility_name: e.target.value }))}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State *</Label>
                <Select
                  value={newProspect.state}
                  onValueChange={(v) => setNewProspect((p) => ({ ...p, state: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {mockStates.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Facility Type *</Label>
                <Select
                  value={newProspect.facility_type}
                  onValueChange={(v) => setNewProspect((p) => ({ ...p, facility_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FACILITY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={newProspect.city}
                  onChange={(e) => setNewProspect((p) => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="main_phone">Phone</Label>
                <Input
                  id="main_phone"
                  value={newProspect.main_phone}
                  onChange={(e) => setNewProspect((p) => ({ ...p, main_phone: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={newProspect.website}
                onChange={(e) => setNewProspect((p) => ({ ...p, website: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select
                  value={newProspect.priority}
                  onValueChange={(v) => setNewProspect((p) => ({ ...p, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={newProspect.status}
                  onValueChange={(v) => setNewProspect((p) => ({ ...p, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="interested">Interested</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                rows={3}
                value={newProspect.notes}
                onChange={(e) => setNewProspect((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddProspect(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addingProspect}>
                {addingProspect ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Prospect'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={(open) => { setShowImport(open); if (!open) { setImportText(''); setImportResult(null); setImportError(''); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Prospects (CSV)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload a CSV file or paste CSV text with a header row. Required columns: <code className="text-xs bg-gray-100 px-1 rounded">facility_name</code>,{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">state</code>. Optional: website, main_phone, facility_type,
              contact_name, contact_email, contact_phone, contact_title, notes.
            </p>
            <div>
              <Label htmlFor="csv-file" className="text-xs">Upload CSV file</Label>
              <Input id="csv-file" type="file" accept=".csv,text/csv" onChange={handleImportFileChange} />
            </div>
            <Textarea
              rows={10}
              placeholder={'facility_name,state,main_phone\nExample Recovery Center,CA,555-000-1111'}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              className="font-mono text-xs"
            />
            {importError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-700">{importError}</AlertDescription>
              </Alert>
            )}
            {importResult && (
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-700">
                  Imported {importResult.imported}, skipped {importResult.skipped} (already existed).
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Close</Button>
            <Button onClick={handleImportSubmit} disabled={importing || !importText.trim()}>
              {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</> : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reports Dialog */}
      <Dialog open={showReports} onOpenChange={setShowReports}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Prospect Reports</DialogTitle>
          </DialogHeader>
          {loadingReports ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : !reports || Object.keys(reports.by_status || {}).length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No prospects yet - reports will populate as you add them.</p>
          ) : (
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-sm text-gray-900 mb-2">By Status</h4>
                <div className="space-y-1">
                  {Object.entries(reports.by_status).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-gray-600 capitalize">{k.replace('_', ' ')}</span>
                      <span className="font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-gray-900 mb-2">By Priority</h4>
                <div className="space-y-1">
                  {Object.entries(reports.by_priority).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-gray-600 capitalize">{k}</span>
                      <span className="font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-gray-900 mb-2">By State</h4>
                <div className="space-y-1">
                  {Object.entries(reports.by_state).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-gray-600">{k}</span>
                      <span className="font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activity Log Dialog */}
      <Dialog open={showActivity} onOpenChange={setShowActivity}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Recent Activity</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 mb-2">
            <Select value={activityTypeFilter} onValueChange={setActivityTypeFilter}>
              <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="proposal">Proposal</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="followup">Follow-up</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" className="w-36 h-8 text-xs" value={activityDateFrom} onChange={(e) => setActivityDateFrom(e.target.value)} />
            <Input type="date" className="w-36 h-8 text-xs" value={activityDateTo} onChange={(e) => setActivityDateTo(e.target.value)} />
            <Input placeholder="Filter by facility..." className="flex-1 h-8 text-xs" value={activitySearch} onChange={(e) => setActivitySearch(e.target.value)} />
          </div>
          {loadingActivity ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No interactions logged yet.</p>
          ) : filteredActivity.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No activity matches your filters.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredActivity.map((a) => (
                <div key={a.id} className="p-3 border rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{a.facility_name || 'Unknown facility'}</span>
                    <Badge variant="outline" className="text-xs capitalize">{a.interaction_type}</Badge>
                  </div>
                  <p className="text-gray-700 mt-1">{a.subject}</p>
                  {a.outcome && <p className="text-gray-500 text-xs mt-1">Outcome: {a.outcome}</p>}
                  <p className="text-gray-400 text-xs mt-1">
                    {formatTimestamp(a.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Callback Requests Dialog */}
      <Dialog open={showCallbackRequests} onOpenChange={setShowCallbackRequests}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Callback Requests</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <Select value={callbackSourceFilter} onValueChange={setCallbackSourceFilter}>
              <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="web">Web</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={callbackHideContacted}
                onChange={(e) => setCallbackHideContacted(e.target.checked)}
              />
              Hide contacted
            </label>
          </div>
          {loadingCallbackRequests ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : callbackRequests.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No callback requests yet.</p>
          ) : filteredCallbackRequests.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No callback requests match your filters.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredCallbackRequests.map((r) => (
                <div key={r.id} className="p-3 border rounded-lg text-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-semibold">{r.name}</span>{' '}
                      <Badge variant="outline" className="text-xs capitalize ml-1">{r.source}</Badge>
                      {r.contacted ? (
                        <Badge className="bg-green-100 text-green-800 border-0 text-xs ml-1">Contacted</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 border-0 text-xs ml-1">Pending</Badge>
                      )}
                    </div>
                    {!r.contacted && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkContacted(r.id)}
                        disabled={markingContactedId === r.id}
                      >
                        {markingContactedId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Mark Contacted'}
                      </Button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-gray-600 mt-2 text-xs">
                    <div className="flex items-center"><Phone className="h-3 w-3 mr-1" />{r.phone}</div>
                    {r.email && <div className="flex items-center"><Mail className="h-3 w-3 mr-1" />{r.email}</div>}
                    {r.state && <div>State: {r.state}</div>}
                    {r.addiction && <div>Addiction: {r.addiction}</div>}
                    {r.insurance && <div>Insurance: {r.insurance}</div>}
                    {r.urgency && <div>Urgency: {r.urgency}</div>}
                  </div>
                  {r.message && <p className="text-gray-700 text-xs mt-2">"{r.message}"</p>}
                  <p className="text-gray-400 text-xs mt-2">
                    {formatTimestamp(r.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Facility Verifications Dialog */}
      <Dialog open={showFacilityVerifications} onOpenChange={setShowFacilityVerifications}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Facility Verifications</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2 mb-2">
            What Mary's outreach calls found. Nothing here has been applied to the live directory yet - review and update facility records manually.
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <input
              type="checkbox"
              checked={verificationHideReviewed}
              onChange={(e) => setVerificationHideReviewed(e.target.checked)}
            />
            Hide reviewed
          </label>
          {loadingFacilityVerifications ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : facilityVerifications.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No facility verifications yet.</p>
          ) : filteredFacilityVerifications.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No verifications match your filters.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredFacilityVerifications.map((v) => (
                <div key={v.id} className="p-3 border rounded-lg text-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-semibold">{v.facility_name}</span>
                      {v.accurate ? (
                        <Badge className="bg-green-100 text-green-800 border-0 text-xs ml-2">Confirmed Accurate</Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800 border-0 text-xs ml-2">Needs Correction</Badge>
                      )}
                      {v.reviewed && (
                        <Badge className="bg-gray-100 text-gray-800 border-0 text-xs ml-2">Reviewed</Badge>
                      )}
                    </div>
                    {!v.reviewed && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkVerificationReviewed(v.id)}
                        disabled={markingReviewedId === v.id}
                      >
                        {markingReviewedId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Mark Reviewed'}
                      </Button>
                    )}
                  </div>
                  {v.corrections && <p className="text-gray-700 text-xs mt-2"><strong>Correction:</strong> {v.corrections}</p>}
                  {v.notes && <p className="text-gray-600 text-xs mt-1">{v.notes}</p>}
                  <p className="text-gray-400 text-xs mt-2">
                    {formatTimestamp(v.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Buyer Qualifications Dialog */}
      <Dialog open={showBuyerQualifications} onOpenChange={setShowBuyerQualifications}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Buyer Qualifications</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2 mb-2">
            Every outcome from Mary's qualification calls. Only $999+/call confirmed-decision-maker responses are auto-added to the CRM as prospects - everything else lives here for manual follow-up.
          </p>
          <Select value={qualificationFilter} onValueChange={setQualificationFilter}>
            <SelectTrigger className="w-56 h-8 text-xs mb-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outcomes</SelectItem>
              <SelectItem value="qualified">Qualified ($999+, in CRM)</SelectItem>
              <SelectItem value="decision_maker_unreachable">Decision Maker Not Reached</SelectItem>
              <SelectItem value="not_interested">Reached, Not Interested</SelectItem>
            </SelectContent>
          </Select>
          {loadingBuyerQualifications ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : buyerQualifications.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No qualification calls logged yet.</p>
          ) : filteredBuyerQualifications.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No calls match your filter.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredBuyerQualifications.map((q) => (
                <div key={q.id} className="p-3 border rounded-lg text-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-semibold">{q.facility_name}</span>
                      {q.state && <span className="text-gray-500 text-xs ml-1">({q.state})</span>}
                      {q.qualified ? (
                        <Badge className="bg-green-100 text-green-800 border-0 text-xs ml-2">Qualified - in CRM</Badge>
                      ) : !q.decision_maker_confirmed ? (
                        <Badge className="bg-yellow-100 text-yellow-800 border-0 text-xs ml-2">Decision Maker Not Reached</Badge>
                      ) : q.interested === false ? (
                        <Badge className="bg-gray-100 text-gray-800 border-0 text-xs ml-2">Not Interested</Badge>
                      ) : q.price_per_call != null ? (
                        <Badge className="bg-orange-100 text-orange-800 border-0 text-xs ml-2">Below $999 (${q.price_per_call}/call)</Badge>
                      ) : null}
                    </div>
                  </div>

                  {!q.decision_maker_confirmed ? (
                    <div className="text-xs text-gray-600 mt-2 space-y-0.5">
                      {q.decision_maker_name && <div>Decision maker: {q.decision_maker_name}</div>}
                      {q.decision_maker_contact && <div>Contact: {q.decision_maker_contact}</div>}
                      {q.best_time_to_call && <div>Best time to call: {q.best_time_to_call}</div>}
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-x-4 gap-y-0.5 text-gray-600 mt-2 text-xs">
                      {q.price_per_call != null && <div>Price: ${q.price_per_call}/call</div>}
                      {q.min_call_duration && <div>Min duration: {q.min_call_duration}</div>}
                      {q.business_hours && <div>Hours: {q.business_hours}</div>}
                      {q.service_area && <div>Service area: {q.service_area}</div>}
                      {q.treatment_types && <div>Treatment types: {q.treatment_types}</div>}
                      {(q.calls_per_day || q.calls_per_week || q.calls_per_month) && (
                        <div>
                          Capacity: {[q.calls_per_day && `${q.calls_per_day}/day`, q.calls_per_week && `${q.calls_per_week}/week`, q.calls_per_month && `${q.calls_per_month}/month`].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                  {q.notes && <p className="text-gray-600 text-xs mt-2">"{q.notes}"</p>}
                  <p className="text-gray-400 text-xs mt-2">
                    {formatTimestamp(q.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Do Not Call List Dialog */}
      <Dialog open={showDoNotCall} onOpenChange={setShowDoNotCall}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Do Not Call List</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2 mb-2">
            Facilities excluded from every future Mary campaign batch - added automatically when a facility asks not to be called again or declines to verify their listing, or manually below.
          </p>
          <div className="flex gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={handleCopyDnc} disabled={doNotCallList.length === 0}>
              {dncCopied ? 'Copied!' : 'Copy List'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportDnc} disabled={doNotCallList.length === 0}>
              <FileDown className="h-4 w-4 mr-1" />Export CSV
            </Button>
          </div>

          <div className="border rounded-lg p-3 space-y-2 mb-3">
            <p className="text-xs font-medium text-gray-700">Add manually</p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Facility name *"
                value={newDncEntry.facility_name}
                onChange={(e) => setNewDncEntry((d) => ({ ...d, facility_name: e.target.value }))}
              />
              <Input
                placeholder="State (optional)"
                maxLength={2}
                value={newDncEntry.state}
                onChange={(e) => setNewDncEntry((d) => ({ ...d, state: e.target.value.toUpperCase() }))}
              />
              <Input
                placeholder="Phone (optional)"
                value={newDncEntry.main_phone}
                onChange={(e) => setNewDncEntry((d) => ({ ...d, main_phone: e.target.value }))}
              />
              <Input
                placeholder="Reason (optional)"
                value={newDncEntry.reason}
                onChange={(e) => setNewDncEntry((d) => ({ ...d, reason: e.target.value }))}
              />
            </div>
            <Button size="sm" onClick={handleAddDnc} disabled={addingDnc || !newDncEntry.facility_name.trim()}>
              {addingDnc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : 'Add to List'}
            </Button>
          </div>

          {loadingDoNotCall ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : doNotCallList.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No facilities on the do-not-call list.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {doNotCallList.map((d) => (
                <div key={d.id} className="p-3 border rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{d.facility_name}</span>
                    <Badge variant="outline" className="text-xs capitalize">
                      {d.source === 'mary_call' ? 'Asked not to be called' : d.source === 'mary_call_declined' ? 'Declined to verify' : 'Manual'}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {d.state && <span>{d.state} · </span>}
                    {d.main_phone && <span>{d.main_phone}</span>}
                  </div>
                  {d.reason && <p className="text-gray-600 text-xs mt-1">"{d.reason}"</p>}
                  <p className="text-gray-400 text-xs mt-1">{formatTimestamp(d.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Call Logs Dialog */}
      <Dialog open={showCallLogs} onOpenChange={(open) => { setShowCallLogs(open); if (!open) setExpandedCallId(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Call Logs</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2 mb-2">
            Every call across Manu, Mornock, and Mary - transcripts, recordings, and outcomes.
          </p>
          <Select value={callLogAgentFilter} onValueChange={setCallLogAgentFilter}>
            <SelectTrigger className="w-40 h-8 text-xs mb-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              <SelectItem value="agent_b464c9c116429482cd1526707d">Manu</SelectItem>
              <SelectItem value="agent_4c1fa450c2a29088ba5bde405d">Mornock</SelectItem>
              <SelectItem value="agent_21fb8c47a48ec7d41b0144bafd">Mary</SelectItem>
            </SelectContent>
          </Select>
          {loadingCallLogs ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : callLogs.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No calls logged yet.</p>
          ) : filteredCallLogs.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No calls match this filter.</p>
          ) : (
            <div className="space-y-2 max-h-[28rem] overflow-y-auto">
              {filteredCallLogs.map((c) => (
                <div key={c.call_id} className="border rounded-lg text-sm">
                  <button
                    className="w-full text-left p-3 flex items-start justify-between hover:bg-gray-50"
                    onClick={() => setExpandedCallId(expandedCallId === c.call_id ? null : c.call_id)}
                  >
                    <div>
                      <span className="font-semibold">{AGENT_NAMES[c.agent_id] || 'Unknown agent'}</span>
                      <Badge variant="outline" className="text-xs capitalize ml-2">{c.direction}</Badge>
                      {c.call_successful === true && <Badge className="bg-green-100 text-green-800 border-0 text-xs ml-1">Successful</Badge>}
                      {c.call_successful === false && c.disconnection_reason === 'voicemail_reached' && <Badge className="bg-amber-100 text-amber-800 border-0 text-xs ml-1">Message Left</Badge>}
                      {c.call_successful === false && c.disconnection_reason !== 'voicemail_reached' && <Badge className="bg-red-100 text-red-800 border-0 text-xs ml-1">Unsuccessful</Badge>}
                      <p className="text-gray-600 text-xs mt-1">
                        {c.direction === 'inbound' ? `From ${c.from_number}` : `To ${c.to_number}`}
                        {c.duration_seconds != null && ` · ${c.duration_seconds}s`}
                      </p>
                      {c.call_summary && <p className="text-gray-700 text-xs mt-1">{c.call_summary}</p>}
                    </div>
                    <span className="text-gray-400 text-xs whitespace-nowrap ml-2">
                      {c.start_timestamp ? new Date(c.start_timestamp).toLocaleString() : ''}
                    </span>
                  </button>
                  {expandedCallId === c.call_id && (
                    <div className="border-t p-3 bg-gray-50">
                      {c.recording_url && (
                        <audio controls src={c.recording_url} className="w-full mb-2 h-8" />
                      )}
                      <p className="text-xs text-gray-500 mb-1">Disconnection: {c.disconnection_reason || 'unknown'}</p>
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">
                        {c.transcript || 'No transcript available.'}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Queue Dialog */}
      <Dialog open={showCampaignQueue} onOpenChange={setShowCampaignQueue}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Queue</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2 mb-2">
            Mary's daily outreach - today's progress, who's up next, and manual controls.
          </p>
          {loadingCampaignQueue || !campaignQueue ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : (
            <>
              <div className="flex items-center justify-between p-3 border rounded-lg mb-3 bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-700">Status:</span>
                  {campaignQueue.paused ? (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">Paused</Badge>
                  ) : campaignQueue.run_status === 'running' ? (
                    <Badge className="bg-green-100 text-green-800 border-0 text-xs">Running</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800 border-0 text-xs">Idle</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  {campaignQueue.paused ? (
                    <Button size="sm" variant="outline" onClick={handleResumeCampaign} disabled={campaignActionLoading}>
                      Resume
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={handlePauseCampaign} disabled={campaignActionLoading}>
                      Pause
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleRunCampaignNow}
                    disabled={campaignActionLoading || campaignQueue.paused || campaignQueue.run_status === 'running'}
                  >
                    Run Now
                  </Button>
                </div>
              </div>

              <div className="p-3 border rounded-lg mb-3">
                <p className="text-xs font-medium text-gray-700 mb-1.5">Retell balance (manually recorded)</p>
                {accountBalance?.recorded ? (
                  <p className="text-xs text-gray-600 mb-2">
                    ${accountBalance.balance_usd?.toFixed(2)} as of {formatTimestamp(accountBalance.updated_at)} -{' '}
                    {accountBalance.mary_calls_allowed
                      ? <span className="text-green-700">Mary's calls allowed</span>
                      : <span className="text-red-700">Mary's calls blocked ({accountBalance.reason})</span>}
                  </p>
                ) : (
                  <p className="text-xs text-red-700 mb-2">No balance recorded yet - Mary's calls are blocked until one is entered.</p>
                )}
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Balance from Retell dashboard, e.g. 27.09"
                    value={newBalanceInput}
                    onChange={(e) => setNewBalanceInput(e.target.value)}
                    className="h-8 text-xs"
                  />
                  <Button size="sm" onClick={handleSaveBalance} disabled={savingBalance || !newBalanceInput}>
                    {savingBalance ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-3 border rounded-lg text-center">
                  <p className="text-2xl font-bold">{campaignQueue.placed_count}<span className="text-sm text-gray-400">/{campaignQueue.daily_limit}</span></p>
                  <p className="text-xs text-gray-500">Called today</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <p className="text-2xl font-bold">{campaignQueue.remaining_today}</p>
                  <p className="text-xs text-gray-500">Remaining today</p>
                </div>
              </div>

              <p className="text-xs font-medium text-gray-700 mb-1">Called today ({campaignQueue.placed_today.length})</p>
              {campaignQueue.placed_today.length === 0 ? (
                <p className="text-sm text-gray-500 py-2 mb-3">No calls placed yet today.</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
                  {campaignQueue.placed_today.map((d) => (
                    <div key={d._id} className="text-xs flex justify-between border-b py-1">
                      <span>{d.facility_name} <span className="text-gray-400">({d.state})</span></span>
                      <span className="text-gray-400">{formatTimestamp(d.called_at)}</span>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs font-medium text-gray-700 mb-1">Up next ({campaignQueue.upcoming_preview.length})</p>
              {campaignQueue.upcoming_preview.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">Nothing queued - either today's limit is reached or the directory is exhausted.</p>
              ) : (
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {campaignQueue.upcoming_preview.map((f, i) => (
                    <div key={i} className="text-xs flex justify-between border-b py-1">
                      <span>{f.name} <span className="text-gray-400">({f.city}, {f.state})</span></span>
                      <span className="text-gray-400">{f.phone}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Prospect Detail / Edit Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailData?.prospect?.facility_name || 'Prospect'}</DialogTitle>
          </DialogHeader>
          {loadingDetail || !editFields ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : (
            <div className="space-y-6">
              {/* Edit fields */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-gray-900">Facility Info</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Facility Name</Label>
                    <Input value={editFields.facility_name} onChange={(e) => setEditFields((f) => ({ ...f, facility_name: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={editFields.main_phone} onChange={(e) => setEditFields((f) => ({ ...f, main_phone: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Website</Label>
                    <Input value={editFields.website} onChange={(e) => setEditFields((f) => ({ ...f, website: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input value={editFields.city} onChange={(e) => setEditFields((f) => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={editFields.status} onValueChange={(v) => setEditFields((f) => ({ ...f, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="interested">Interested</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                        <SelectItem value="negotiating">Negotiating</SelectItem>
                        <SelectItem value="closed_won">Closed Won</SelectItem>
                        <SelectItem value="closed_lost">Closed Lost</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <Select value={editFields.priority} onValueChange={(v) => setEditFields((f) => ({ ...f, priority: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={editFields.notes} onChange={(e) => setEditFields((f) => ({ ...f, notes: e.target.value }))} />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={handleSaveEdit} disabled={savingEdit}>
                    {savingEdit ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
                  </Button>
                  {!detailData.prospect.is_archived ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { handleArchiveProspect(detailData.prospect.id); setShowDetail(false); }}
                    >
                      Archive
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestoreProspect(detailData.prospect.id).then(() => openDetail(detailData.prospect.id))}
                      >
                        Restore
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => { handleDeleteProspect(detailData.prospect.id, detailData.prospect.facility_name); setShowDetail(false); }}
                      >
                        Delete Permanently
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Contacts */}
              <div>
                <h4 className="font-semibold text-sm text-gray-900 mb-2">Contacts</h4>
                {detailData.contacts.length === 0 ? (
                  <p className="text-sm text-gray-500">No contacts on file.</p>
                ) : (
                  <div className="space-y-2">
                    {detailData.contacts.map((c) => (
                      <div key={c.id} className="text-sm flex items-center justify-between border rounded p-2">
                        <span>{c.first_name} {c.last_name} {c.title ? `- ${c.title}` : ''}</span>
                        {c.email && (
                          <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline flex items-center">
                            <Mail className="h-3 w-3 mr-1" />{c.email}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Log interaction */}
              <div>
                <h4 className="font-semibold text-sm text-gray-900 mb-2">Log an Interaction</h4>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={newInteraction.interaction_type} onValueChange={(v) => setNewInteraction((i) => ({ ...i, interaction_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="proposal">Proposal</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="followup">Follow-up</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Subject"
                      value={newInteraction.subject}
                      onChange={(e) => setNewInteraction((i) => ({ ...i, subject: e.target.value }))}
                    />
                  </div>
                  <Textarea
                    rows={2}
                    placeholder="Details / outcome"
                    value={newInteraction.description}
                    onChange={(e) => setNewInteraction((i) => ({ ...i, description: e.target.value }))}
                  />
                  <Button size="sm" variant="outline" onClick={handleLogInteraction} disabled={loggingInteraction || !newInteraction.subject.trim()}>
                    {loggingInteraction ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Logging...</> : 'Log Interaction'}
                  </Button>
                </div>

                {detailData.recent_interactions.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {detailData.recent_interactions.map((i) => (
                      <div key={i.id} className="text-xs border rounded p-2 text-gray-600">
                        <span className="font-medium capitalize">{i.interaction_type}</span>: {i.subject}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add task */}
              <div>
                <h4 className="font-semibold text-sm text-gray-900 mb-2">Add a Task</h4>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    className="col-span-2"
                    placeholder="Task title"
                    value={newTask.title}
                    onChange={(e) => setNewTask((t) => ({ ...t, title: e.target.value }))}
                  />
                  <Input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask((t) => ({ ...t, due_date: e.target.value }))}
                  />
                </div>
                <Button size="sm" variant="outline" className="mt-2" onClick={handleAddTaskForProspect} disabled={addingTask || !newTask.title.trim() || !newTask.due_date}>
                  {addingTask ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : 'Add Task'}
                </Button>

                {detailData.active_tasks.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {detailData.active_tasks.map((t) => (
                      <div key={t.id} className="text-xs text-gray-600">
                        {t.title} - due {new Date(t.due_date).toLocaleDateString()}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Bulk Action - {selectedProspects.length} prospect{selectedProspects.length !== 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {bulkAction === 'update_status' && (
              <Select value={bulkValue} onValueChange={setBulkValue}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="interested">Interested</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                  <SelectItem value="negotiating">Negotiating</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            )}
            {bulkAction === 'update_priority' && (
              <Select value={bulkValue} onValueChange={setBulkValue}>
                <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            )}
            {bulkAction === 'assign_to' && (
              <Input placeholder="Assignee name" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} />
            )}
            {bulkAction === 'add_tags' && (
              <Input placeholder="Tags, comma-separated" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkConfirm} disabled={bulkSubmitting || !bulkValue.trim()}>
              {bulkSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Applying...</> : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Add Task Dialog */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Prospect</Label>
              <Select value={globalNewTask.prospect_id} onValueChange={(v) => setGlobalNewTask((t) => ({ ...t, prospect_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select a prospect" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {prospects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.facility_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {prospects.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">No prospects loaded - clear filters or search first.</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={globalNewTask.title} onChange={(e) => setGlobalNewTask((t) => ({ ...t, title: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input type="date" value={globalNewTask.due_date} onChange={(e) => setGlobalNewTask((t) => ({ ...t, due_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={globalNewTask.priority} onValueChange={(v) => setGlobalNewTask((t) => ({ ...t, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTask(false)}>Cancel</Button>
            <Button
              onClick={handleAddGlobalTask}
              disabled={addingGlobalTask || !globalNewTask.prospect_id || !globalNewTask.title.trim() || !globalNewTask.due_date}
            >
              {addingGlobalTask ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : 'Add Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMDashboard;